import { registerDecorators as _registerDecorators, registerComponent as _registerComponent, LightningElement } from "lwc";
import _tmpl from "./handler.html";
import { WireStore } from '@oneappexchange/lwc-wired';
import equalityFn from 'zustand/shallow';
import { AnalyticsEventTopic, AnalyticsEventType } from '~/modules/analytics/analyticsHelper';
import { AnalyticsService } from '~/modules/services/analyticsService';
import { analyticsStore, selectListingData } from '~/modules/stores/analyticsStore';
import { listingStore, selectListing } from '~/modules/stores/listingStore';
import { routerStore } from '~/modules/stores/routerStore';
import { selectAuthenticationFlow, selectIsGuestUser, userStore } from '~/modules/stores/userStore';
import { logger } from '~/modules/utilities/logger';
class AnalyticsHandler extends LightningElement {
  constructor(...args) {
    super(...args);
    this.authenticated = false;
    this.authenticationDetails = {
      flow: undefined,
      origin: undefined
    };
    this.lastPageViewUrl = null;
    this.analyticsService = new AnalyticsService();
    this.onAnalyticsEvent = event => {
      if (event.detail) {
        const listingId = event.detail.currentListingId;
        // If we have a listing and is not public do an early
        // return to break execution
        if (listingId && !selectListing(listingStore.getState(), listingId)?.public) {
          return;
        }
        const {
          eventType
        } = event.detail;
        // Defined by EventType type on instrument.ts
        switch (eventType) {
          case AnalyticsEventType.CLICK:
            this.handleClick(event);
            break;
          case AnalyticsEventType.TILE_SHOWN:
            this.handleShown(event);
            break;
          case AnalyticsEventType.TILE_LOADED:
            this.handleLoaded(event);
            break;
          case AnalyticsEventType.SEARCH:
            this.handleSearch(event);
            break;
          case AnalyticsEventType.IMAGE_SHOWN:
            this.handleImageShown(event);
            break;
          case AnalyticsEventType.CHAT_INIT:
            this.handleChatInit(event);
            break;
          case AnalyticsEventType.FILTER_KEYWORD_CHANGE:
            this.handleFilterKeywordChange(event);
            break;
          case AnalyticsEventType.LEAD:
            this.handleLead(event);
            break;
          case AnalyticsEventType.USER_ACTIVITY:
            this.handleUserActivity(event);
            break;
          case AnalyticsEventType.USER_INSTALL:
            this.handleUserInstall(event);
            break;
          default:
            logger.error('Ignored event type:' + eventType);
            break;
        }
      }
    };
    // @ts-expect-error * Automated Type Error Fix
    this.getBaseEvent = event => {
      return {
        // @ts-expect-error * Automated Type Error Fix
        authenticated: this.authenticated,
        ...event.detail,
        ...selectListingData(event.detail?.currentListingId),
        ...this.analyticsService.generateBaseAnalyticEvent()
      };
    };
    this.createPageView = routerState => {
      const event = {
        detail: {}
      };
      const sendEvent = {
        ...this.getBaseEvent(event),
        topic: AnalyticsEventTopic.ANALYTICS_PAGE_LOAD,
        coveo: {
          eventAction: routerState.url
        }
      };
      this.analyticsService.sendEvent(sendEvent);
    };
    this.handleAuthentication = () => {
      const {
        tbidProfile
      } = userStore.getState();
      this.analyticsService.sendEvent({
        topic: 'authentication',
        ...this.getBaseEvent({
          detail: {
            id: tbidProfile?.custom_attributes?.user_id,
            orgId: tbidProfile?.organization_id,
            company: tbidProfile?.custom_attributes?.CompanyName,
            relationship: tbidProfile?.custom_attributes?.RelationshipToSalesforce,
            role: tbidProfile?.custom_attributes?.Role,
            locale: tbidProfile?.locale
          }
        })
      });
    };
    // @ts-expect-error * Automated Type Error Fix
    this.handleClick = event => {
      const analyticsEvent = {
        topic: AnalyticsEventTopic.ANALYTICS_CLICK,
        ...this.getBaseEvent(event)
      };
      this.analyticsService.sendEvent(analyticsEvent);
    };
    // @ts-expect-error * Automated Type Error Fix
    this.handleLoaded = event => {
      const analyticsEvent = {
        topic: AnalyticsEventTopic.ANALYTICS_LOAD_IMPRESSION,
        ...this.getBaseEvent(event)
      };
      this.analyticsService.sendEvent(analyticsEvent);
    };
    // @ts-expect-error * Automated Type Error Fix
    this.handleFilterKeywordChange = event => {
      const analyticsEvent = {
        topic: AnalyticsEventTopic.ANALYTICS_FILTER_SEARCH,
        ...this.getBaseEvent(event)
      };
      this.analyticsService.sendEvent(analyticsEvent);
    };
    // @ts-expect-error * Automated Type Error Fix
    this.handleShown = event => {
      const analyticsEvent = {
        topic: AnalyticsEventTopic.ANALYTICS_SHOWN_IMPRESSION,
        ...this.getBaseEvent(event)
      };
      this.analyticsService.sendEvent(analyticsEvent);
    };
    // @ts-expect-error * Automated Type Error Fix
    this.handleSearch = event => {
      const analyticsEvent = {
        topic: AnalyticsEventTopic.ANALYTICS_SEARCH_DONE,
        ...this.getBaseEvent(event)
      };
      this.analyticsService.sendEvent(analyticsEvent);
    };
    this.handleTBIDAuthentication = () => {
      const {
        flow,
        origin
      } = this.authenticationDetails;
      const uerState = userStore.getState();
      const {
        user_id,
        CompanyName: tbidCompanyName,
        RelationshipToSalesforce: tbidRelationshipToSalesforce,
        Role: tbidRole,
        SignupService: authenticationMethod
      } = uerState.tbidProfile?.custom_attributes || {};
      this.analyticsService.sendEvent({
        topic: AnalyticsEventTopic.ANALYTICS_AUTHENTICATION,
        eventType: flow,
        authenticationOrigin: origin,
        ...this.getBaseEvent({}),
        userId: user_id,
        company: tbidCompanyName,
        relationship: tbidRelationshipToSalesforce,
        role: tbidRole,
        authenticationMethod
      });
    };
    // @ts-expect-error * Automated Type Error Fix
    this.handleImageShown = event => {
      const analyticsEvent = {
        ...this.getBaseEvent(event),
        topic: AnalyticsEventTopic.ANALYTICS_CAROUSEL_SHOWN_IMPRESSION
      };
      this.analyticsService.sendEvent(analyticsEvent);
    };
    // @ts-expect-error * Automated Type Error Fix
    this.handleChatInit = event => {
      const analyticsEvent = {
        topic: AnalyticsEventTopic.ANALYTICS_CHAT_INIT,
        ...this.getBaseEvent(event)
      };
      this.analyticsService.sendEvent(analyticsEvent);
    };
  }
  async setUserAuthenticated(isGuestUser) {
    this.authenticated = !isGuestUser;
    this.handleAuthentication();
  }
  async handleAuthenticationFlow({
    auth_flow,
    auth_origin,
    isGuestUser
  }) {
    if (!this.authenticationDetails.flow && !this.authenticationDetails.origin) {
      this.authenticated = !isGuestUser;
      this.authenticationDetails = {
        flow: auth_flow,
        origin: auth_origin
      };
      this.handleTBIDAuthentication();
    }
  }
  handlePageView(state) {
    // We also cannot blindly send a pageView each time this is called because on init it seems to be called twice
    if (state.sendPageView && !state.pageViewSent &&
    // trigger page views only when they are public
    selectListing(listingStore.getState(), state.listingId)?.public) {
      this.createPageView(routerStore.getState());
      analyticsStore.pageViewSent();
    }
  }
  handleLead(event) {
    const analyticsEvent = {
      topic: AnalyticsEventTopic.ANALYTICS_LEAD_EVENT,
      ...this.getBaseEvent(event)
    };
    this.analyticsService.sendEvent(analyticsEvent);
  }
  handleUserActivity(event) {
    const analyticsEvent = {
      topic: AnalyticsEventTopic.ANALYTICS_USER_ACTIVITY,
      ...this.getBaseEvent(event)
    };
    this.analyticsService.sendEvent(analyticsEvent);
  }
  handleUserInstall(event) {
    const analyticsEvent = {
      topic: AnalyticsEventTopic.ANALYTICS_USER_INSTALL,
      ...this.getBaseEvent(event)
    };
    this.analyticsService.sendEvent(analyticsEvent);
  }
  /*LWC compiler v7.1.3*/
}
_registerDecorators(AnalyticsHandler, {
  wire: {
    setUserAuthenticated: {
      adapter: WireStore,
      dynamic: [],
      method: 1,
      config: function ($cmp) {
        return {
          store: userStore,
          selector: selectIsGuestUser
        };
      }
    },
    handleAuthenticationFlow: {
      adapter: WireStore,
      dynamic: [],
      method: 1,
      config: function ($cmp) {
        return {
          store: userStore,
          selector: selectAuthenticationFlow,
          equalityFn
        };
      }
    },
    handlePageView: {
      adapter: WireStore,
      dynamic: [],
      method: 1,
      config: function ($cmp) {
        return {
          store: analyticsStore
        };
      }
    }
  },
  fields: ["authenticated", "authenticationDetails", "lastPageViewUrl", "analyticsService", "onAnalyticsEvent", "getBaseEvent", "createPageView", "handleAuthentication", "handleClick", "handleLoaded", "handleFilterKeywordChange", "handleShown", "handleSearch", "handleTBIDAuthentication", "handleImageShown", "handleChatInit"]
});
const __lwc_component_class_internal = _registerComponent(AnalyticsHandler, {
  tmpl: _tmpl,
  sel: "",
  apiVersion: 62
});
export default __lwc_component_class_internal;